/**
 * Crew Chat — the cross-project chat hub.
 *
 * One screen where crew, supervisors, and admins reach EVERY project crew-chat
 * they can access — including projects no one has messaged yet, so the first
 * message can be started right here. Pick a project to read and post, jump to
 * it, and attach/view files — the detail pane reuses TaskChatPanel verbatim, so
 * all of the posting/@mention/attachment/camera behaviour comes for free.
 *
 * Chat moved from the task up to the project (D1), so the hub is per-project.
 * Scope is the same Vari-crew-only boundary as the chat (the PM/requester is
 * never here): crew see only projects they hold a block on; supervisors/admins
 * see every project. A sticky toolbar gives compact search + sort, and
 * completed/cancelled projects are hidden until "Show completed" is toggled. The
 * selection lives in the URL (?project=:projectId) so it deep-links and the
 * mobile Back button works.
 *
 * Master–detail, responsive: desktop (md+) shows the list beside the detail pane;
 * mobile shows the list, then a full-screen detail with Back.
 */
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowDownUp,
  ArrowLeft,
  Briefcase,
  Calendar,
  ExternalLink,
  Loader2,
  Lock,
  MessageSquare,
  MessagesSquare,
  Search,
} from 'lucide-react';
import type { CrewChatThread, ProjectStatus } from '@vari-crew/schemas';
import { useCrewChatThreads } from '@/api/crew-chat';
import { TaskChatPanel } from '@/components/TaskChatPanel';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { formatRelative } from '@/lib/format';
import { cn } from '@/lib/cn';

/** Statuses worth a small pill in the row (scheduled = the "normal" state gets none). */
const STATUS_PILL: Partial<Record<ProjectStatus, string>> = {
  en_route: 'En route',
  on_site: 'In progress',
  punch: 'Punch',
  pending_closeout: 'Wrapping up',
  blocked: 'Blocked',
  complete: 'Completed',
  cancelled: 'Cancelled',
};

type SortMode = 'activity' | 'scheduled' | 'project';

const SORTS: Record<SortMode, (a: CrewChatThread, b: CrewChatThread) => number> = {
  // Freshest first — recent messages, then message-less projects by their schedule.
  activity: (a, b) => b.activity_at.localeCompare(a.activity_at),
  // Soonest scheduled first; unscheduled last; ties broken by recent activity.
  scheduled: (a, b) => {
    if (a.scheduled_start && b.scheduled_start) {
      return (
        a.scheduled_start.localeCompare(b.scheduled_start) ||
        b.activity_at.localeCompare(a.activity_at)
      );
    }
    if (a.scheduled_start) return -1;
    if (b.scheduled_start) return 1;
    return b.activity_at.localeCompare(a.activity_at);
  },
  // Alphabetical by project.
  project: (a, b) =>
    a.project_title.localeCompare(b.project_title) ||
    b.activity_at.localeCompare(a.activity_at),
};

export function CrewChat() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedProjectId = searchParams.get('project') ?? null;

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortMode>('activity');
  const [showCompleted, setShowCompleted] = useState(false);

  const { data, isLoading, error } = useCrewChatThreads(showCompleted);
  const threads = useMemo(() => data?.threads ?? [], [data]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? threads.filter(
          (t) =>
            t.project_title.toLowerCase().includes(q) ||
            t.client_name.toLowerCase().includes(q),
        )
      : threads;
    return [...filtered].sort(SORTS[sort]);
  }, [threads, query, sort]);

  const selected = selectedProjectId
    ? (threads.find((t) => t.project_id === selectedProjectId) ?? null)
    : null;

  function selectProject(projectId: string) {
    // Drive selection through the URL so it deep-links and Back restores the list.
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('project', projectId);
      return next;
    });
  }

  function clearProject() {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('project');
      return next;
    });
  }

  return (
    <div className="h-full flex flex-col bg-fill-soft">
      <header className="px-4 md:px-6 pt-4 pb-3 border-b border-line bg-bg">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-semibold">Crew chat</h1>
          <span className="inline-flex items-center gap-1 rounded-full bg-fill-soft px-2 py-0.5 text-[10px] font-medium text-ink-dim">
            <Lock className="h-3 w-3" />
            Vari crew only
          </span>
        </div>
        <p className="text-sm text-ink-dim mt-0.5">
          Chat on any project you’re on — start one even before the first message. The
          requester doesn’t see these.
        </p>
      </header>

      <div className="flex-1 min-h-0 flex">
        {/* ---- Thread list (master). Hidden on mobile once a thread is open. ---- */}
        <aside
          className={cn(
            'w-full md:w-96 md:flex-shrink-0 md:border-r md:border-line overflow-y-auto',
            selectedProjectId && 'hidden md:block',
          )}
        >
          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-ink-dim p-6">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : error ? (
            <div className="p-6 text-sm text-status-blocked-fg" role="alert">
              {(error as Error).message}
            </div>
          ) : (
            <>
              <Toolbar
                query={query}
                onQuery={setQuery}
                sort={sort}
                onSort={setSort}
                showCompleted={showCompleted}
                onShowCompleted={setShowCompleted}
              />
              {threads.length === 0 ? (
                <EmptyThreads showCompleted={showCompleted} />
              ) : visible.length === 0 ? (
                <NoMatches query={query} />
              ) : (
                <ul className="divide-y divide-line">
                  {visible.map((t) => (
                    <li key={t.project_id}>
                      <ThreadRow
                        thread={t}
                        active={t.project_id === selectedProjectId}
                        onSelect={() => selectProject(t.project_id)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </aside>

        {/* ---- Detail pane. Full-screen on mobile when a thread is selected. ---- */}
        <section
          className={cn(
            'flex-1 min-w-0 overflow-y-auto',
            !selectedProjectId && 'hidden md:block',
          )}
        >
          {selected ? (
            <div className="p-4 md:p-6 space-y-3 max-w-3xl mx-auto">
              <DetailHeader
                projectId={selected.project_id}
                projectTitle={selected.project_title}
                clientName={selected.client_name}
                onBack={clearProject}
              />
              {/* Reuse the full chat experience verbatim, scoped to the project. */}
              <TaskChatPanel scope={{ projectId: selected.project_id }} />
            </div>
          ) : selectedProjectId ? (
            // URL points at a project we can't see (or it's filtered out by the toggle).
            <NoSuchThread onBack={clearProject} />
          ) : (
            <PickAThread hasThreads={threads.length > 0} />
          )}
        </section>
      </div>
    </div>
  );
}

/** Sticky compact controls above the list: search + sort + show-completed. */
function Toolbar({
  query,
  onQuery,
  sort,
  onSort,
  showCompleted,
  onShowCompleted,
}: {
  query: string;
  onQuery: (v: string) => void;
  sort: SortMode;
  onSort: (v: SortMode) => void;
  showCompleted: boolean;
  onShowCompleted: (v: boolean) => void;
}) {
  return (
    <div className="sticky top-0 z-20 bg-bg/95 backdrop-blur border-b border-line px-3 py-2 space-y-2">
      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-faint" />
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search projects, clients…"
          aria-label="Search chats"
          className="w-full rounded-md border border-line bg-fill-soft pl-7 pr-2 py-1.5 text-sm placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>
      <div className="flex items-center justify-between gap-2">
        <label className="inline-flex items-center gap-1 text-[11px] text-ink-dim">
          <ArrowDownUp className="h-3.5 w-3.5 text-ink-faint" />
          <span className="sr-only">Sort by</span>
          <select
            value={sort}
            onChange={(e) => onSort(e.target.value as SortMode)}
            aria-label="Sort by"
            className="bg-transparent text-[11px] font-medium text-ink focus:outline-none cursor-pointer"
          >
            <option value="activity">Recent activity</option>
            <option value="scheduled">Scheduled date</option>
            <option value="project">Project</option>
          </select>
        </label>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-ink-dim cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => onShowCompleted(e.target.checked)}
            className="h-3.5 w-3.5 rounded border-line accent-[var(--color-accent)]"
          />
          Show completed
        </label>
      </div>
    </div>
  );
}

/** One project's chat, summarised as a list row. */
function ThreadRow({
  thread,
  active,
  onSelect,
}: {
  thread: CrewChatThread;
  active: boolean;
  onSelect: () => void;
}) {
  const hasMsg = thread.message_count > 0;
  const statusLabel = STATUS_PILL[thread.project_status];
  const schedShort = thread.scheduled_start
    ? new Date(thread.scheduled_start).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null;
  return (
    <button
      onClick={onSelect}
      className={cn(
        'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-fill-soft',
        active && 'bg-fill-soft',
      )}
    >
      <Briefcase className="h-4 w-4 mt-0.5 flex-shrink-0 text-ink-faint" />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-sm font-medium truncate">{thread.project_title}</span>
          <span className="text-[11px] text-ink-faint whitespace-nowrap flex-shrink-0">
            {hasMsg ? (
              formatRelative(thread.last_message_at!)
            ) : schedShort ? (
              <span className="inline-flex items-center gap-0.5">
                <Calendar className="h-3 w-3" />
                {schedShort}
              </span>
            ) : null}
          </span>
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 min-w-0">
          <span className="text-[11px] text-ink-faint truncate">{thread.client_name}</span>
          {statusLabel && (
            <span className="inline-flex flex-shrink-0 rounded-full bg-fill-soft px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wide text-ink-dim">
              {statusLabel}
            </span>
          )}
        </div>
        <div className="text-xs text-ink-dim truncate mt-0.5">
          {hasMsg ? (
            <>
              <span className="text-ink-faint">{thread.last_author_name}:</span>{' '}
              {thread.last_message_excerpt}
            </>
          ) : (
            <span className="italic text-ink-faint">
              No messages yet — start the conversation.
            </span>
          )}
        </div>
      </div>
      {hasMsg && (
        <span className="inline-flex items-center gap-1 self-center text-[11px] text-ink-faint flex-shrink-0">
          <MessageSquare className="h-3 w-3" />
          {thread.message_count}
        </span>
      )}
    </button>
  );
}

/** Compact header above the chat panel — project + client, with the jump link. */
function DetailHeader({
  projectId,
  projectTitle,
  clientName,
  onBack,
}: {
  projectId: string;
  projectTitle: string;
  clientName: string;
  onBack: () => void;
}) {
  return (
    <div className="space-y-2">
      {/* Mobile-only Back to the list (the URL drops ?project). */}
      <button
        onClick={onBack}
        className="md:hidden inline-flex items-center gap-1 text-sm text-ink-dim hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" /> All chats
      </button>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold truncate">{projectTitle}</h2>
          <div className="text-sm text-ink-dim truncate">{clientName}</div>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <Button asChild variant="outline" size="sm">
            <Link to={`/projects/${projectId}`}>
              <ExternalLink className="h-3.5 w-3.5" /> Open project
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/** No projects at all — the whole-list empty state. */
function EmptyThreads({ showCompleted }: { showCompleted: boolean }) {
  return (
    <div className="p-8 text-center text-sm text-ink-dim">
      <MessagesSquare className="h-8 w-8 mx-auto mb-2 text-ink-faint" />
      No projects to chat on yet.
      {!showCompleted && (
        <div className="mt-1 text-xs text-ink-faint">
          Completed projects are hidden — toggle “Show completed” to see them.
        </div>
      )}
    </div>
  );
}

/** Search matched nothing. */
function NoMatches({ query }: { query: string }) {
  return (
    <div className="p-8 text-center text-sm text-ink-dim">
      <Search className="h-7 w-7 mx-auto mb-2 text-ink-faint" />
      No projects match “{query.trim()}”.
    </div>
  );
}

/** Desktop placeholder when nothing is selected. */
function PickAThread({ hasThreads }: { hasThreads: boolean }) {
  return (
    <div className="h-full grid place-items-center p-8">
      <div className="text-center text-sm text-ink-dim max-w-xs">
        <MessagesSquare className="h-10 w-10 mx-auto mb-3 text-ink-faint" />
        {hasThreads
          ? 'Pick a project from the list to read and post.'
          : 'No projects to chat on yet.'}
      </div>
    </div>
  );
}

/** The ?project id doesn't resolve to a visible thread (gone, or filtered out). */
function NoSuchThread({ onBack }: { onBack: () => void }) {
  return (
    <div className="h-full grid place-items-center p-8">
      <Card className="p-6 text-center text-sm text-ink-dim max-w-xs">
        That chat isn’t available.
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-3.5 w-3.5" /> Back to chats
          </Button>
        </div>
      </Card>
    </div>
  );
}
